/**
 * Direct deep-link screen for Contact Work lanes and capacity.
 */
import { formatInteger } from "./format.js";
import { fetchJson, fetchSavedViews } from "./dashboard-lead-factory-fetch.js";
import { applyUrlSavedViews } from "./dashboard-saved-view-apply.js";
import {
  contactWorkCapacityUrl,
  contactWorkOperatorEventsUrl,
  contactWorkOperatorOutcomesUrl,
  contactWorkQueueUrl,
  contactWorkThroughputUrl,
  getContactWorkFilters
} from "./dashboard-saved-view-state.js";
import {
  leadFactoryContactLetterPacketsUrl,
  leadFactoryContactWorkCapacityUrl,
  leadFactoryContactWorkOperatorEventsUrl,
  leadFactoryContactWorkOperatorOutcomesUrl,
  leadFactoryContactWorkQueueUrl,
  leadFactoryContactWorkThroughputUrl
} from "./dashboard-urls.js";
import { contactWorkSavedViewControlsHtml } from "./dashboard-saved-views.js";
import { contactWorkLaneHtml } from "./dashboard-contact-lanes.js";
import { isReturnedMailRetryLetterTask } from "./dashboard-command-center.js";
import {
  contactWorkBulkAssignmentHtml,
  contactWorkCapacityHtml,
  contactWorkCompletionPressureHtml,
  contactWorkDailyPlanHtml,
  contactWorkOperatorAccountabilityHtml,
  contactWorkOperatorActionHistoryHtml,
  contactWorkOperatorOutcomeReadbackHtml,
  contactWorkOperatorSummaryHtml,
  contactWorkSummaryHtml,
  contactWorkThroughputHtml
} from "./dashboard-contact-work.js";
import type {
  LeadFactoryContactLetterPacketsResponse,
  LeadFactoryContactWorkCapacityResponse,
  LeadFactoryContactWorkOperatorEventsResponse,
  LeadFactoryContactWorkOperatorOutcomesResponse,
  LeadFactoryContactWorkThroughputResponse,
  LeadFactoryTasksResponse
} from "./dashboard-lead-factory-types.js";

export function directContactWorkRequested(): boolean {
  const value = new URLSearchParams(window.location.search).get("lf_contact_work_direct")?.trim().toLowerCase() ?? "";
  return value === "1" || value === "true";
}

export async function renderDirectContactWork(
  mount: HTMLElement,
  bindActions: (statusElement: HTMLElement) => void
): Promise<void> {
  await applyUrlSavedViews();
  const contactWorkFilters = getContactWorkFilters();
  const [contactWorkSavedViews, contactWorkQueue, contactWorkCapacity, contactWorkThroughput, contactWorkOperatorEvents, contactWorkOperatorOutcomes, contactLetterPackets] =
    await Promise.all([
      fetchSavedViews("contact_work"),
      fetchJson<LeadFactoryTasksResponse>(contactWorkQueueUrl(leadFactoryContactWorkQueueUrl, contactWorkFilters)),
      fetchJson<LeadFactoryContactWorkCapacityResponse>(contactWorkCapacityUrl(leadFactoryContactWorkCapacityUrl, contactWorkFilters)),
      fetchJson<LeadFactoryContactWorkThroughputResponse>(contactWorkThroughputUrl(leadFactoryContactWorkThroughputUrl, contactWorkFilters)),
      fetchJson<LeadFactoryContactWorkOperatorEventsResponse>(contactWorkOperatorEventsUrl(leadFactoryContactWorkOperatorEventsUrl, contactWorkFilters)),
      fetchJson<LeadFactoryContactWorkOperatorOutcomesResponse>(contactWorkOperatorOutcomesUrl(leadFactoryContactWorkOperatorOutcomesUrl, contactWorkFilters)),
      fetchJson<LeadFactoryContactLetterPacketsResponse>(leadFactoryContactLetterPacketsUrl)
    ]);
  mount.innerHTML = `
    <main class="dashboard-shell contact-work-direct" data-contact-work-direct>
      <section class="panel">
        <div class="panel-heading">
          <div>
            <h1>Contact Work</h1>
            <p class="subtle">${formatInteger(contactWorkQueue.summary?.loaded ?? contactWorkQueue.tasks.length)} loaded · ${formatInteger(contactWorkQueue.summary?.ready ?? contactWorkQueue.tasks.length)} ready</p>
          </div>
          <a class="button secondary" href="/">Dashboard</a>
        </div>
        ${contactWorkSavedViewControlsHtml(contactWorkSavedViews.views, contactWorkFilters)}
        ${contactWorkSummaryHtml(contactWorkQueue.summary)}
        ${contactWorkCapacityHtml(contactWorkCapacity)}
        ${contactWorkCompletionPressureHtml(contactWorkCapacity, contactWorkThroughput)}
        ${contactWorkDailyPlanHtml(contactWorkCapacity, contactWorkThroughput, contactWorkFilters)}
        ${contactWorkOperatorAccountabilityHtml(contactWorkCapacity, contactWorkThroughput, contactWorkFilters)}
        ${contactWorkOperatorActionHistoryHtml(contactWorkOperatorEvents)}
        ${contactWorkOperatorOutcomeReadbackHtml(contactWorkOperatorOutcomes)}
        ${contactWorkThroughputHtml(contactWorkThroughput)}
        ${contactWorkOperatorSummaryHtml(contactWorkQueue.summary)}
        ${contactWorkBulkAssignmentHtml(contactWorkQueue.tasks, contactWorkCapacity)}
        <div class="contact-work-grid">
          ${contactWorkLaneHtml("Calls", contactWorkQueue.tasks.filter((task) => task.task_type === "call_owner"), contactLetterPackets.packets, contactWorkFilters.leadId)}
          ${contactWorkLaneHtml("Retry Letters", contactWorkQueue.tasks.filter(isReturnedMailRetryLetterTask), contactLetterPackets.packets, contactWorkFilters.leadId)}
          ${contactWorkLaneHtml("Letters", contactWorkQueue.tasks.filter((task) => task.task_type === "prepare_letter" && !isReturnedMailRetryLetterTask(task)), contactLetterPackets.packets, contactWorkFilters.leadId)}
          ${contactWorkLaneHtml("Skip Trace", contactWorkQueue.tasks.filter((task) => task.task_type === "skip_trace"), contactLetterPackets.packets, contactWorkFilters.leadId)}
          ${contactWorkLaneHtml("PI", contactWorkQueue.tasks.filter((task) => task.task_type === "pi_research"), contactLetterPackets.packets, contactWorkFilters.leadId)}
        </div>
      </section>
      <aside id="county-detail" class="detail-drawer" aria-live="polite"></aside>
      <p id="action-status" class="action-status" role="status" aria-live="polite">Contact Work loaded.</p>
    </main>
  `;
  const statusElement = document.querySelector<HTMLElement>("#action-status");
  if (!statusElement) throw new Error("Missing dashboard element #action-status");
  bindActions(statusElement);
}
